import React from 'react';
import { Link } from 'react-router-dom';
import './ISO9001.css';

const ISO9001 = () => {
  return (
    <div className="page-container">
      <div className="page-content">
        <div className="page-main">
          <section className="page-section">
            <div className="page-breadcrumb">System Certification</div>
            <h1 className="page-title">ISO 9001 Quality Management System</h1>
            <h2 className="page-subtitle">NQC guarantee in ISO 9001 Standard</h2>
            <div className="page-text-content">
              <p>
                ISO 9001 represents an international standard for Quality Management Systems (QMS), 
                providing a framework for organizations to establish, implement, maintain, and 
                continuously improve their quality management systems. The primary goals are to enhance 
                product/service quality, ensure customer satisfaction, and effectively manage business processes.
              </p>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Why is ISO 9001 Important?</h2>
            <div className="page-text-content">
              <p>
                ISO (International Organization for Standardization) is a body that develops globally 
                recognized standards. ISO 9001 certification is an official document verifying business 
                adherence to these standards. Here's why certification is crucial:
              </p>
            </div>
            <div className="importance-list">
              <div className="importance-item">
                <h3 className="importance-title">Global Competitiveness</h3>
                <p className="importance-description">
                  ISO 9001 certification helps businesses compete internationally by demonstrating 
                  compliance with globally recognized quality standards, opening doors to new markets 
                  and business opportunities.
                </p>
              </div>
              <div className="importance-item">
                <h3 className="importance-title">Customer Trust</h3>
                <p className="importance-description">
                  Certification signals a commitment to quality and safety, boosting customer loyalty 
                  and confidence in your products and services.
                </p>
              </div>
              <div className="importance-item">
                <h3 className="importance-title">Continuous Improvement</h3>
                <p className="importance-description">
                  ISO 9001 encourages efficiency, process optimization, and cost reduction through 
                  systematic quality management practices.
                </p>
              </div>
              <div className="importance-item">
                <h3 className="importance-title">Legal and Regulatory Compliance</h3>
                <p className="importance-description">
                  In many industries, ISO 9001 certification is a prerequisite for meeting legal 
                  requirements and regulatory standards.
                </p>
              </div>
              <div className="importance-item">
                <h3 className="importance-title">Business Continuity and Risk Management</h3>
                <p className="importance-description">
                  The standard helps organizations plan for business resilience, manage risks 
                  effectively, and ensure continuous operation.
                </p>
              </div>
              <div className="importance-item">
                <h3 className="importance-title">Innovation and Competitive Advantage</h3>
                <p className="importance-description">
                  ISO 9001 promotes innovation and helps businesses stand out in competitive markets 
                  by demonstrating commitment to excellence.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Principles of ISO 9001</h2>
            <div className="page-text-content">
              <p>
                ISO 9001 standards are based on several core principles aimed at ensuring high-quality 
                product and service delivery:
              </p>
            </div>
            <div className="principles-list">
              <div className="principle-item">
                <h3 className="principle-title">1. Customer Focus</h3>
                <p className="principle-description">
                  Ensuring that customer needs and expectations are understood and met, thereby 
                  enhancing customer satisfaction.
                </p>
              </div>
              <div className="principle-item">
                <h3 className="principle-title">2. Leadership</h3>
                <p className="principle-description">
                  Leaders are responsible for establishing a vision and direction for the organization, 
                  creating an environment that encourages achieving quality objectives.
                </p>
              </div>
              <div className="principle-item">
                <h3 className="principle-title">3. Engagement of People</h3>
                <p className="principle-description">
                  The involvement of all employees is crucial for the effectiveness of the QMS, 
                  encouraging participation and motivation.
                </p>
              </div>
              <div className="principle-item">
                <h3 className="principle-title">4. Process Approach</h3>
                <p className="principle-description">
                  Managing activities and resources as processes helps achieve more consistent and 
                  predictable results.
                </p>
              </div>
              <div className="principle-item">
                <h3 className="principle-title">5. Continuous Improvement</h3>
                <p className="principle-description">
                  An ongoing focus on improving processes and systems enhances performance over time.
                </p>
              </div>
              <div className="principle-item">
                <h3 className="principle-title">6. Evidence-Based Decision Making</h3>
                <p className="principle-description">
                  Effective decisions are based on the analysis of data and information, ensuring 
                  informed strategic planning.
                </p>
              </div>
              <div className="principle-item">
                <h3 className="principle-title">7. Relationship Management</h3>
                <p className="principle-description">
                  Maintaining a mutually beneficial relationship with interested parties, such as 
                  suppliers, enhances performance and success.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Advantages of ISO 9001 Certification</h2>
            <div className="advantages-list">
              <div className="advantage-item">
                <h3 className="advantage-title">Competitive Advantage</h3>
                <p className="advantage-description">
                  Demonstrates compliance with quality standards, giving a business a competitive edge.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Customer Confidence</h3>
                <p className="advantage-description">
                  Provides assurance to customers that the business is reliable and quality-focused.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Increased Efficiency</h3>
                <p className="advantage-description">
                  Standardizes processes and promotes continuous improvement, leading to higher efficiency.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Legal Compliance</h3>
                <p className="advantage-description">
                  Helps ensure adherence to legal and regulatory requirements.
                </p>
              </div>
              <div className="advantage-item">
                <h3 className="advantage-title">Business Continuity</h3>
                <p className="advantage-description">
                  Enhances the organization's ability to manage risks and ensure continuous operation.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">How to Obtain ISO 9001 Certification</h2>
            <div className="page-text-content">
              <p>
                Obtaining ISO 9001 certification involves several steps:
              </p>
            </div>
            <div className="steps-list">
              <div className="step-item">
                <h3 className="step-title">1. Determine the Appropriate Standard</h3>
                <p className="step-description">
                  Identify which ISO standard applies to your business.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">2. Select a Certification Body</h3>
                <p className="step-description">
                  Choose a reputable certification organization to guide you through the process.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">3. Implement the Standard</h3>
                <p className="step-description">
                  Align your processes with the ISO 9001 requirements.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">4. Internal Audits and Documentation</h3>
                <p className="step-description">
                  Conduct internal audits and prepare documentation.
                </p>
              </div>
              <div className="step-item">
                <h3 className="step-title">5. External Audits</h3>
                <p className="step-description">
                  Undergo audits by the certification body to verify compliance.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Conclusion</h2>
            <div className="page-text-content">
              <p>
                ISO 9001 is a cornerstone for businesses across various sectors, fostering customer 
                satisfaction, process optimization, and business continuity. Implementing an effective 
                QMS based on ISO 9001 standards helps organizations stay competitive, efficient, and 
                resilient in a global market. For businesses aiming to shape their future and thrive 
                in the competitive landscape, obtaining ISO 9001 certification is a strategic move.
              </p>
            </div>
          </section>
        </div>

        <aside className="page-sidebar">
          <div className="sidebar-section">
            <h2 className="sidebar-title">Our Services</h2>
            <ul className="sidebar-links">
              <li><Link to="/system-certification">System Certification</Link></li>
              <li><Link to="/product-certification">Product Certification</Link></li>
              <li><Link to="/quality-certification">Quality Certification</Link></li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ISO9001;

